<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quiz.title }}</h2>
      <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
      <div class="question-box">
        <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
        <ul class="options-list">
          <li
            v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
            :key="index"
            @click="selectOption(index)"
            :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
            class="option"
          >
            {{ option }}
            <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
        <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
          </li>
        </ul>
      </div>
      <div v-if="isAnswerRevealed" class="feedback">
        <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
          {{ feedbackMessage }}
        </p>
        
      </div>
      <div class="navigation-buttons">
        <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
        <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
      </div>
      <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
        <h4>Quiz Summary</h4>
        <p>Correct answers: {{ correctAnswersCount }}</p>
        <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
        <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
        <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
      </div>
    </div><br><br><br><br><br><br>
  </template>
  
  <script>
import { ref } from 'vue';

export default {
  setup() {
  
    const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/nyse.m4a');
    const quiz = ref({
      title: "Introduction to Investing in Canada",
      questions: [
        {
          question: "What is the main goal of investing?",
          options: [
            "To save money for short-term needs",
            "To make purchases with high credit limits",
            "To grow your money over time",
            "To keep your money safe under the mattress"
          ],
          correctAnswer: 2
        },
        {
          question: "Which of the following is a common type of investment in Canada?",
          options: [
            "Savings accounts",
            "Stocks and bonds",
            "Commodities like gold and oil",
            "All of the above"
          ],
          correctAnswer: 3
        },
        {
          question: "What does diversification in investing mean?",
          options: [
            "Investing all your money in one asset",
            "Spreading your investments across various assets to reduce risk",
            "Choosing investments from different countries",
            "Investing in digital currencies only"
          ],
          correctAnswer: 1
        },
        {
          question: "What is a TFSA?",
          options: [
            "Tax-Free Savings Account, a savings vehicle that allows your investments to grow tax-free",
            "Tax-Funded Savings Account, a government-provided savings account",
            "Total Financial Savings Allocation, a method of budgeting",
            "Tax-Free Stock Accumulation, a stock investment plan"
          ],
          correctAnswer: 0
        },
        {
          question: "What is an RRSP?",
          options: [
            "Registered Retirement Savings Plan, a retirement savings plan that offers tax benefits",
            "Rapid Return Savings Program, a high-risk investment scheme",
            "Real Estate Savings Plan, a plan dedicated to real estate investments",
            "Registered Risk Sharing Plan, a government-backed investment risk sharing program"
          ],
          correctAnswer: 0
        },
        {
          question: "Why is it important to review and adjust your investment portfolio regularly?",
          options: [
            "To ensure it aligns with your changing financial goals and risk tolerance",
            "Because the government requires it annually",
            "To avoid taxes on investments",
            "There is no need to review the portfolio regularly"
          ],
          correctAnswer: 0
        },
        {
          question: "What is a mutual fund?",
          options: [
            "A government-funded pension program",
            "A private savings account with fixed interest",
            "An investment vehicle made up of a pool of funds collected from many investors",
            "A tax-free inheritance fund"
          ],
          correctAnswer: 2
        },
        {
          question: "What role does the Canada Revenue Agency (CRA) play in investing?",
          options: [
            "It provides investment advice to Canadian citizens",
            "It regulates and taxes investment earnings and contributions in Canada",
            "It directly manages individual investment accounts",
            "It offers financial grants to investors"
          ],
          correctAnswer: 1
        },
        {
          question: "How can compound interest affect your investments?",
          options: [
            "It decreases the value of investments over time due to inflation",
            "It allows you to earn interest on both your original investment and the interest it has already earned",
            "It applies only to savings accounts, not investments",
            "It is a penalty charged on late investment payments"
          ],
          correctAnswer: 1
        },
        {
          question: "What is a bond?",
          options: [
            "A type of loan from an investor to a borrower such as a corporation or government",
            "A guarantee that an investment will double in value",
            "A high-risk investment that offers guaranteed returns",
            "A method of directly purchasing stock in a company"
          ],
          correctAnswer: 0
        }
      ]
    });
    const currentQuestionIndex = ref(0);
    const correctAnswersCount = ref(0);
    const incorrectAnswersCount = ref(0);
    const selectedOptionIndex = ref(-1);
    const isAnswerRevealed = ref(false);
    const isCorrect = ref(false);
    const feedbackMessage = ref("");

    const selectOption = (index) => {
      if (isAnswerRevealed.value) return;
      selectedOptionIndex.value = index;
      isAnswerRevealed.value = true;

      if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
        isCorrect.value = true;
        correctAnswersCount.value++;
        feedbackMessage.value = "Correct!";
        correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
      } else {
        isCorrect.value = false;
        incorrectAnswersCount.value++;
        feedbackMessage.value = "Incorrect.";
      }
    };

    const nextQuestion = () => {
      if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
        currentQuestionIndex.value++;
        resetState();
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        currentQuestionIndex.value--;
        resetState();
      }
    };

    const retakeQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswersCount.value = 0;
      incorrectAnswersCount.value = 0;
      resetState();
    };

    const resetState = () => {
      selectedOptionIndex.value = -1;
      isAnswerRevealed.value = false;
      isCorrect.value = false;
      feedbackMessage.value = "";
    };

    return {
      quiz,
      currentQuestionIndex,
      correctAnswersCount,
      incorrectAnswersCount,
      selectedOptionIndex,
      isAnswerRevealed,
      isCorrect,
      feedbackMessage,
      selectOption,
      nextQuestion,
      previousQuestion,
      retakeQuiz,
    };
  },
};
</script>

 
<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>